import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
}

export interface ModalConfig {
  id: string;
  title: string;
  content?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closable?: boolean;
  onClose?: () => void;
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Theme
  theme: 'light' | 'dark' | 'system';

  // Modals
  activeModals: ModalConfig[];

  // Notifications
  notifications: Notification[];

  // Loading states
  globalLoading: boolean;
  loadingMessage?: string;

  // Page-specific
  currentPage: string;
  pageTitle: string;
  breadcrumbs: { label: string; href?: string }[];

  // Preferences
  tablePageSize: number;
  compactMode: boolean;
}

interface UIActions {
  // Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapse: () => void;

  // Theme
  setTheme: (theme: UIState['theme']) => void;

  // Modals
  openModal: (config: ModalConfig) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;

  // Notifications
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Loading
  setGlobalLoading: (loading: boolean, message?: string) => void;

  // Page
  setCurrentPage: (page: string, title: string, breadcrumbs?: UIState['breadcrumbs']) => void;

  // Preferences
  setTablePageSize: (size: number) => void;
  setCompactMode: (compact: boolean) => void;
}

type UIStore = UIState & UIActions;

const initialState: UIState = {
  sidebarOpen: false,
  sidebarCollapsed: false,
  theme: 'light',
  activeModals: [],
  notifications: [],
  globalLoading: false,
  loadingMessage: undefined,
  currentPage: '',
  pageTitle: '',
  breadcrumbs: [],
  tablePageSize: 20,
  compactMode: false,
};

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Sidebar actions
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),

      toggleSidebarCollapse: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      // Theme actions
      setTheme: (theme: UIState['theme']) => {
        set({ theme });
        // Apply theme to document
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else if (theme === 'light') {
          document.documentElement.classList.remove('dark');
        } else {
          // System preference
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          if (prefersDark) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
      },

      // Modal actions
      openModal: (config: ModalConfig) =>
        set((state) => ({
          activeModals: [...state.activeModals, config],
        })),

      closeModal: (id: string) =>
        set((state) => {
          const modal = state.activeModals.find((m) => m.id === id);
          if (modal?.onClose) {
            modal.onClose();
          }
          return {
            activeModals: state.activeModals.filter((m) => m.id !== id),
          };
        }),

      closeAllModals: () => {
        const { activeModals } = get();
        activeModals.forEach((modal) => {
          if (modal.onClose) {
            modal.onClose();
          }
        });
        set({ activeModals: [] });
      },

      // Notification actions
      addNotification: (notification: Omit<Notification, 'id'>) => {
        const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newNotification: Notification = {
          id,
          duration: 5000,
          dismissible: true,
          ...notification,
        };

        set((state) => ({
          notifications: [...state.notifications, newNotification],
        }));

        // Auto-dismiss after duration
        if (newNotification.duration && newNotification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, newNotification.duration);
        }

        return id;
      },

      removeNotification: (id: string) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),

      // Loading actions
      setGlobalLoading: (loading: boolean, message?: string) =>
        set({ globalLoading: loading, loadingMessage: message }),

      // Page actions
      setCurrentPage: (
        page: string,
        title: string,
        breadcrumbs: UIState['breadcrumbs'] = []
      ) =>
        set({
          currentPage: page,
          pageTitle: title,
          breadcrumbs,
        }),

      // Preference actions
      setTablePageSize: (size: number) => set({ tablePageSize: size }),

      setCompactMode: (compact: boolean) => set({ compactMode: compact }),
    }),
    {
      name: 'warehouse-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        tablePageSize: state.tablePageSize,
        compactMode: state.compactMode,
      }),
    }
  )
);

// Selector hooks for common use cases
export const useSidebar = () =>
  useUIStore((state) => ({
    isOpen: state.sidebarOpen,
    isCollapsed: state.sidebarCollapsed,
    toggle: state.toggleSidebar,
    setOpen: state.setSidebarOpen,
    toggleCollapse: state.toggleSidebarCollapse,
  }));

export const useNotifications = () =>
  useUIStore((state) => ({
    notifications: state.notifications,
    add: state.addNotification,
    remove: state.removeNotification,
    clear: state.clearNotifications,
  }));

export const useModals = () =>
  useUIStore((state) => ({
    modals: state.activeModals,
    open: state.openModal,
    close: state.closeModal,
    closeAll: state.closeAllModals,
  }));

export default useUIStore;
